import { Fragment, useEffect, useState } from "react";
import { UIMatch, useMatches, useNavigate } from "react-router";

import { Box } from "@design-system/Box/Box";
import { Clickable } from "@design-system/Clickable/Clickable";
import { Icon } from "@design-system/Icon/Icon";
import { Text } from "@design-system/Text/Text";

import { routes } from "@pages/routes";

import classes from "./Breadcrumbs.module.css";

type BreadcrumbLabel = {
  title?: React.ReactNode;
  left?: React.ReactNode;
  /** Reset the breadcrumb to this as the first breadcrumb */
  reset?: boolean;
  /** Override the default path */
  path?: string;
};

export type Breadcrumb = {
  label: BreadcrumbLabel;
  path?: string;
};

type Handle = {
  breadcrumb?:
    | BreadcrumbLabel
    | ((match: UIMatch) => BreadcrumbLabel | Promise<BreadcrumbLabel>);
};

// eslint-disable-next-line react-refresh/only-export-components
export const useBreadcrumbs = () => {
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);
  const matches = useMatches() as UIMatch<unknown, Handle>[];

  useEffect(() => {
    (async () => {
      const allBreadcrumbs = await Promise.all(
        matches
          .filter((match) => match.handle?.breadcrumb)
          .map(async (match) => {
            const breadcrumb = match.handle?.breadcrumb;

            const resolvedBreadcrumb = typeof breadcrumb === "function"
              ? await breadcrumb(match)
              : breadcrumb;

            return {
              label: resolvedBreadcrumb ?? {
                title: "",
                left: undefined,
              },
              path: resolvedBreadcrumb?.path ?? (match.pathname.endsWith("/")
                ? match.pathname.slice(0, -1)
                : match.pathname),
            };
          }),
      );

      const lastResetIndex =
        allBreadcrumbs
          .map((bc, index) => ({ bc, index }))
          .reverse()
          .find(({ bc }) => bc.label.reset)?.index ?? -1;

      const finalBreadcrumbs =
        lastResetIndex >= 0
          ? allBreadcrumbs.slice(lastResetIndex)
          : allBreadcrumbs;

      const homeBreadcrumb: Breadcrumb = {
        label: {
          title: "",
          left: <Icon icon="faHouse" size="xs" />,
        },
        path: routes.portal.dashboard.$path(),
      };

      setBreadcrumbs([homeBreadcrumb, ...finalBreadcrumbs]);
    })();
  }, [matches]);

  return breadcrumbs;
};

const Breadcrumbs = () => {
  const breadcrumbs = useBreadcrumbs();
  const navigate = useNavigate();

  const currentBreadcrumb = breadcrumbs[breadcrumbs.length - 1];

  const handleClick = (path?: string) => {
    if (path) {
      navigate(path, { viewTransition: true });
    }
  };

  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <Box direction="column" gap={4}>
      <Box align="center" gap={4}>
        <Text size={18} weight={500} spacing="relaxed">
          {currentBreadcrumb?.label.left} {currentBreadcrumb?.label.title}
        </Text>
      </Box>
      <div className={classes.breadcrumbs}>
        {breadcrumbs?.map((breadcrumb, index) => {
          const isLast = index === breadcrumbs.length - 1;

          const text = (
            <Text
              size={11}
              weight={500}
              color={breadcrumb.path && !isLast ? "textSecondary" : "textDark"}
            >
              {breadcrumb.label.left} {breadcrumb.label.title}
            </Text>
          );

          const content =
            breadcrumb.path && !isLast ? (
              <Clickable
                onClick={() => handleClick(breadcrumb.path)}
                outline="xs"
              >
                <Box align="center" gap={4}>
                  {text}
                </Box>
              </Clickable>
            ) : (
              text
            );

          return (
            <Fragment key={index}>
              {content}
              {!isLast && (
                <Text size={10} weight={400} color="textSecondary">
                  /
                </Text>
              )}
            </Fragment>
          );
        })}
      </div>
    </Box>
  );
};

export default Breadcrumbs;
